import isNil from 'lodash.isnil'
import omitBy from 'lodash.omitby'
import { getField, updateField } from 'vuex-map-fields'

import permissions from '@/constants/permissions'
import childEdition from '@/services/http/api/childEdition'
import resource from '@/services/http/api/resource'
import { FetchPeselPayloadSchema, UpdatePeselPayloadSchema } from '@/schemas/childEdition'

const child = (childEditionData, schoolHydraUsers) => {
  if (childEditionData == null && schoolHydraUsers == null) return null
  return {
    child_edition: childEditionData || {},
    school_hydra_users: schoolHydraUsers || {}
  }
}

export default {
  namespaced: true,
  state: {
    loader: null,
    modalLoader: null,
    changeSchoolModalVisibility: false,
    revokePublicationModalVisibility: false,
    updateChildBaseDataModalVisibility: false,
    updateChildBaseDataModalForm: null,
    excludeChildModalVisibility: false,
    excludeModal: {
      data: {
        exclude: null,
        excludeOther: null
      },
      resource: null
    },
    includeAgainChildModalVisibility: false,
    child: child(),
    childPesel: null,
    childEditionRequestSkipped: false,
    paymentsList: null,
    paymentsListLoader: false,
    isPeselModalVisible: false
  },
  getters: {
    getLoader: (state) => state.loader,
    getChild: (state) => state.child,
    getChildPesel: (state) => state.childPesel,
    getChildsEditionName: (state) => state?.child?.child_edition?.edition?.name || null,
    getModalLoader: (state) => state.modalLoader,
    getChangeSchoolModalVisibility: (state) => state.changeSchoolModalVisibility,
    getRevokePublicationModalVisibility: (state) => state.revokePublicationModalVisibility,
    getUpdateChildBaseDataModalVisibility: (state) => state.updateChildBaseDataModalVisibility,
    getUpdateChildBaseDataModalForm: (state) => state.updateChildBaseDataModalForm,
    getExcludeChildModalVisibility: (state) => state.excludeChildModalVisibility,
    getExcludeModalDataField: (state) => getField(state.excludeModal.data),
    getExcludeModalResource: (state) => state.excludeModal.resource,
    getIncludeAgainChildModalVisibility: (state) => state.includeAgainChildModalVisibility,
    isChildEditionRequestSkipped: (state) => state.childEditionRequestSkipped,
    getPaymentsList: (state) => state.paymentsList,
    getPaymentsListLoader: (state) => state.paymentsListLoader,
    getIsPeselModalVisible: (state) => state.isPeselModalVisible,
    hasViewAndEditPeselPermission: (state, getters, rootState, rootGetters) =>
      rootGetters['auth/userHasPermission'](permissions.peselViewAndEdit)
  },
  mutations: {
    setLoader(state, payload) {
      state.loader = payload
    },
    setModalLoader(state, payload) {
      state.modalLoader = payload
    },
    setChangeSchoolModalVisibility(state, payload) {
      state.changeSchoolModalVisibility = payload
    },
    setRevokePublicationModalVisibility(state, payload) {
      state.revokePublicationModalVisibility = payload
    },
    setUpdateChildBaseDataModalVisibility(state, payload) {
      state.updateChildBaseDataModalVisibility = payload
    },
    setUpdateChildBaseDataModalForm(state, payload) {
      state.updateChildBaseDataModalForm = payload
    },
    setExcludeChildModalVisibility(state, payload) {
      state.excludeChildModalVisibility = payload
    },
    updateExcludeModalDataField(state, field) {
      updateField(state.excludeModal.data, field)
    },
    setExcludeChildResource(state, payload) {
      state.excludeModal.resource = payload
    },
    setIncludeAgainChildModalVisibility(state, payload) {
      state.includeAgainChildModalVisibility = payload
    },
    setChild(state, payload) {
      state.child = child(payload.child_edition, payload.school_hydra_users)
    },
    setChildPesel(state, payload) {
      state.childPesel = payload
    },
    clear(state) {
      state.child = null
      state.loader = null
      state.childPesel = null
    },
    setChildEditionRequestSkipped(state, payload) {
      state.childEditionRequestSkipped = payload
    },
    setPaymentsList(state, payload) {
      state.paymentsList = payload
    },
    setPaymentsListLoader(state, payload) {
      state.paymentsListLoader = payload
    },
    clearPaymentsList(state) {
      state.paymentsList = null
    },
    setIsPeselModalVisible(state, payload) {
      state.isPeselModalVisible = payload
    }
  },
  actions: {
    async getChild({ commit, dispatch }, payload) {
      if (Number.isInteger(Number(payload.childEditionId)) === false) {
        const error = new Error('invalid child edition id')
        error.response = { status: 404 }
        return Promise.reject(error)
      }
      commit('setLoader', true)

      if (payload?.isCoordinator && childEdition?.getForCoordinator) {
        return childEdition
          .getForCoordinator(payload)
          .then(({ data }) => {
            commit('setChild', data?.data)
            return data?.data
          })
          .finally(() => {
            commit('setLoader', false)
          })
      }
      return childEdition
        .get(payload, payload?.getAssignedFirst)
        .then(({ data }) => {
          commit('setChild', data?.data)
          return data?.data
        })
        .catch((error) => {
          if (
            error?.response?.status === 403 &&
            payload?.getAssignedFlag &&
            !payload?.getAssignedFirst
          ) {
            dispatch('getChild', { ...payload, getAssignedFirst: true })
          } else throw error
        })
        .finally(() => {
          commit('setLoader', false)
        })
    },
    async getChildsEditionName({ dispatch, getters }, payload) {
      let editionName = getters?.getChildsEditionName || null
      if (isNil(editionName) && payload?.childEditionId) {
        try {
          await dispatch('getChild', { ...payload })
        } catch (error) {
          return Promise.reject(error)
        }
        editionName = getters?.getChildsEditionName || null
      }
      return editionName
    },
    changeSchool({ commit, getters }, payload) {
      commit('setModalLoader', true)
      return childEdition
        .changeSchool(payload)
        .then(({ data }) => {
          const { school_hydra_users: schoolHydraUsers } = getters.getChild
          commit('setChild', {
            child_edition: data.data,
            school_hydra_users: schoolHydraUsers
          })
        })
        .finally(() => {
          commit('setModalLoader', false)
          commit('setChangeSchoolModalVisibility', false)
        })
    },
    revokePublication({ commit, getters }, payload) {
      commit('setModalLoader', true)
      return childEdition
        .revokePublication(payload)
        .then(({ data }) => {
          const { school_hydra_users: schoolHydraUsers } = getters.getChild
          commit('setChild', {
            child_edition: data.data,
            school_hydra_users: schoolHydraUsers
          })
        })
        .finally(() => {
          commit('setModalLoader', false)
          commit('setRevokePublicationModalVisibility', false)
        })
    },
    openUpdateChildBaseDataModal({ commit }, payload) {
      commit('setUpdateChildBaseDataModalForm', payload)
      commit('setUpdateChildBaseDataModalVisibility', true)
    },
    closeUpdateChildBaseDataModal({ commit }) {
      commit('setUpdateChildBaseDataModalVisibility', false)
      commit('setModalLoader', false)
      commit('setUpdateChildBaseDataModalForm', null)
    },
    updatePersonalData({ commit, getters, dispatch }, payload) {
      commit('setModalLoader', true)
      return childEdition
        .updatePersonalData(payload)
        .then(({ data }) => {
          const { school_hydra_users: schoolHydraUsers } = getters.getChild
          commit('setChild', {
            child_edition: data.data,
            school_hydra_users: schoolHydraUsers
          })
        })
        .finally(() => {
          dispatch('closeUpdateChildBaseDataModal')
        })
    },
    updateGuardianData({ commit, getters, dispatch }, payload) {
      commit('setModalLoader', true)
      return childEdition
        .updateGuardianData(payload)
        .then(({ data }) => {
          const { school_hydra_users: schoolHydraUsers } = getters.getChild
          commit('setChild', {
            child_edition: data.data,
            school_hydra_users: schoolHydraUsers
          })
        })
        .finally(() => {
          dispatch('closeUpdateChildBaseDataModal')
        })
    },
    getExcludeChildResource({ commit }) {
      return resource.excludeChildReasons().then(({ data }) => {
        commit('setExcludeChildResource', data.data)
      })
    },
    excludeChild({ commit, getters }, payload) {
      commit('setModalLoader', true)
      const payloadData = payload
      payloadData.data = omitBy(payload.data, isNil)
      return childEdition
        .excludeChild(payloadData)
        .then(({ data }) => {
          const { school_hydra_users: schoolHydraUsers } = getters.getChild
          commit('setChild', {
            child_edition: data.data,
            school_hydra_users: schoolHydraUsers
          })
        })
        .finally(() => {
          commit('setModalLoader', false)
          commit('setExcludeChildModalVisibility', false)
        })
    },
    includeAgainChild({ commit, getters }, payload) {
      commit('setModalLoader', true)
      return childEdition
        .includeAgainChild(payload)
        .then(({ data }) => {
          const { school_hydra_users: schoolHydraUsers } = getters.getChild
          commit('setChild', {
            child_edition: data.data,
            school_hydra_users: schoolHydraUsers
          })
        })
        .finally(() => {
          commit('setModalLoader', false)
          commit('setIncludeAgainChildModalVisibility', false)
        })
    },
    getPaymentsList({ commit }, childEditionId) {
      commit('setPaymentsListLoader', true)
      return childEdition
        .getPaymentsList(childEditionId)
        .then(({ data }) => {
          commit('setPaymentsList', data.data)
          return data.data
        })
        .finally(() => {
          commit('setPaymentsListLoader', false)
        })
    },
    async fetchPesel({ commit, getters }, childEditionId) {
      if (!getters.hasViewAndEditPeselPermission) {
        console.warn('User does not have permission to view pesel')
        return
      }

      const payloadValidation = FetchPeselPayloadSchema.safeParse({ childEditionId })
      if (!payloadValidation.success) {
        console.error('[fetchPesel] Zod Validation Eerror', payloadValidation.error.format())
        return
      }

      try {
        const peselResponse = await childEdition.getPesel(childEditionId)
        const { pesel } = peselResponse.data.data
        commit('setChildPesel', pesel)
      } catch (error) {
        console.error('Failed to fetch pesel', error)
      }
    },
    async updatePesel({ commit, getters }, { childEditionId, pesel }) {
      if (!getters.hasViewAndEditPeselPermission) {
        console.warn('User does not have permission to edit pesel')
        return
      }

      const payloadValidation = UpdatePeselPayloadSchema.safeParse({ childEditionId, pesel })
      if (!payloadValidation.success) {
        console.error('[updatePesel] Zod Validation Eerror', payloadValidation.error.format())
        return
      }

      const payload = { pesel }

      try {
        const peselResponse = await childEdition.updatePesel(childEditionId, payload)
        const childData = peselResponse.data.data
        commit('setChild', childData)
        commit('setChildPesel', childData.child_edition.pesel)
      } catch (error) {
        console.error('Failed to update pesel', error)
      }
    }
  }
}
